import { multiToothKindOf } from "../../lib/procedureFlags";

export const CATEGORY_KEYS = ["crown_units", "bridge", "partial_denture", "full_denture"];

export const CATEGORY_DEFS = {
  crown_units: {
    label: "Crown Units",
    desc: "Same crown / veneer across selected teeth",
    matches: isGroupedCrownProcedure,
    needsMulti: true,
  },
  bridge: {
    label: "Fixed Bridge",
    desc: "Spans adjacent teeth · role-marked (abutment / pontic)",
    matches: (p) => multiToothKindOf(p) === "bridge",
    needsMulti: true,
  },
  partial_denture: {
    label: "Partial Denture",
    desc: "Replaces several teeth in one arch",
    matches: (p) => multiToothKindOf(p) === "partial_denture",
    needsMulti: true,
  },
  full_denture: {
    label: "Full Denture",
    desc: "Whole-arch (upper or lower)",
    matches: (p) => multiToothKindOf(p) === "full_denture",
    needsMulti: false,
  },
};

export function isGroupedCrownProcedure(proc) {
  if (!proc || proc.archived) return false;
  if (multiToothKindOf(proc) !== "crown_unit") return false;
  if (proc.tooth_count !== "single") return false;
  return proc.multi_visit === true || (Number(proc.visit_count) || 1) > 1;
}

export function isMultiVisitMultiTooth(proc) {
  return proc?.tooth_count === "multi"
      && (proc.multi_visit === true || (Number(proc.visit_count) || 1) > 1);
}

export function eligibleMultiToothProcedures(procedures = []) {
  return procedures.filter((p) => {
    if (!p || p.archived) return false;
    if (isGroupedCrownProcedure(p)) return true;
    if (isMultiVisitMultiTooth(p)) return true;
    if (CATEGORY_DEFS.full_denture.matches(p)) return true;
    return false;
  });
}

export function crownUnitProcedures(procedures = []) {
  return procedures
    .filter((p) => !p?.archived && multiToothKindOf(p) === "crown_unit")
    .sort((a, b) => (a.default_fee_centavos || 0) - (b.default_fee_centavos || 0));
}

export function procsForCategory(procedures = [], categoryKey) {
  const def = CATEGORY_DEFS[categoryKey];
  if (!def) return [];
  return procedures.filter(def.matches);
}

export function computeMultiToothFee({
  categoryKey,
  pickedProcedure,
  crownProc = null,
  teethCount = 0,
}) {
  if (!pickedProcedure) return 0;
  if (categoryKey === "bridge") {
    if (!crownProc) return null;
    return (crownProc.default_fee_centavos || 0) * teethCount;
  }
  if (categoryKey === "crown_units") {
    return (pickedProcedure.default_fee_centavos || 0) * teethCount;
  }
  return pickedProcedure.default_fee_centavos || 0;
}

export function buildPlanPayload({
  categoryKey,
  pickedProcedure,
  crownProc = null,
  selectedFdis = [],
  roles = {},
}) {
  const isArchLevel = pickedProcedure?.tooth_count === "none";
  const needsRoles = pickedProcedure?.tooth_role_required === true;
  const teeth = isArchLevel
    ? []
    : selectedFdis.map((fdi) => ({
        tooth_number: String(fdi),
        ...(needsRoles ? { role: roles[fdi] } : {}),
      }));
  const material = categoryKey === "bridge" && crownProc
    ? { procedure_id: crownProc.id }
    : null;
  return {
    procedureId: pickedProcedure?.id,
    teeth,
    material,
  };
}
